import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { classMap } from "lit/directives/class-map.js";
import { resolveColor } from "../helper/utils/color-resolver";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyDividerDirection = "horizontal" | "vertical";

/**
 * @element sky-divider
 *
 * @summary Visual separator component for horizontal and vertical layout separation.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/divider
 *
 * @csspart divider - The divider line element (either `<hr>` or `<div>` depending on direction).
 *
 * @property {string} color - Divider color token or CSS color.
 * @property {string} thickness - Divider thickness (for example `1px` or `2px`).
 * @property {string} margin - Outer margin around the divider.
 * @property {SkyDividerDirection} direction - Divider orientation.
 * @property {boolean} inset - Enables indented divider layout.
 * @property {string} height - Height used for vertical orientation.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method getComputedColor Returns the resolved runtime divider color.
 * @method getComputedThickness Returns the resolved runtime divider thickness.
 *
 * @cssprop --divider-color - CSS custom property for divider color.
 * @cssprop --divider-thickness - CSS custom property for divider thickness.
 * @cssprop --divider-margin - CSS custom property for divider margin.
 * @cssprop --divider-height - CSS custom property for vertical divider height.
 *
 * @Behavior
 * - Simple visual divider that can be horizontal or vertical
 * - Supports extensive customization via properties
 * - Automatically resolves color values using color resolver
 * - Updates CSS custom properties when properties change
 * - **horizontal**: Renders as `<hr>` element, spans full width
 * - **vertical**: Renders as `<div>` element, spans configurable height
 * - Horizontal dividers use semantic `<hr>` element
 * - Vertical dividers use `<div>` with appropriate styling
 *
 * @example
 * ```html
 * <div style="display: flex; align-items: center;">
 *   <span>Left content</span>
 *   <sky-divider
 *     direction="vertical"
 *     height="50px"
 *     margin="0 16px"
 *   ></sky-divider>
 *   <span>Right content</span>
 * </div>
 * ```
 * ```vue
 * <template>
 *   <div style="display:flex;align-items:center;">
 *     <span>Left content</span>
 *     <sky-divider direction="vertical" height="50px" margin="0 16px"></sky-divider>
 *     <span>Right content</span>
 *   </div>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <div style={{ display: "flex", alignItems: "center" }}>
 *       <span>Left content</span>
 *       <sky-divider direction="vertical" height="50px" margin="0 16px"></sky-divider>
 *       <span>Right content</span>
 *     </div>
 *   );
 * }
 * ```
 */
@customElement("sky-divider")
export class SkyDivider extends LitElement {
  /**
   * The color of the divider.
   * @public
   */
  @property({ type: String })
  color = "muted";

  /**
   * The thickness of the divider (e.g., `"1px"`, `"2px"`).
   * @public
   */
  @property({ type: String })
  thickness = "1px";

  /**
   * The margin around the divider.
   * @public
   */
  @property({ type: String })
  margin = "";

  /**
   * Divider orientation.
   * @public
   */
  @property({ type: String })
  direction: SkyDividerDirection = "horizontal";

  /**
   * Whether the divider should be inset (indented).
   * @public
   */
  @property({ type: Boolean })
  inset = false;

  /**
   * The height of a vertical divider.
   * @public
   */
  @property({ type: String })
  height = "100%";


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    hr {
      border: none;
      border-top: var(--divider-thickness, 2px) solid var(--divider-color);
      margin: var(--divider-margin);
    }

    .vertical {
      border-top: none;
      border-left: var(--divider-thickness, 2px) solid var(--divider-color);
      height: var(--divider-height, 100%);
      width: 0 !important;
      margin: var(--divider-margin);
    }

    .inset.horizontal {
      margin-left: 20px;
      margin-right: 20px;
      width: calc(100% - 20px * 2);
    }

    .inset.vertical {
      margin-top: 20px;
      margin-bottom: 20px;
      height: calc(100% - 20px * 2);
    }
  `;

  /** @protected */
  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has("color")) {
      this.style.setProperty("--divider-color", resolveColor(this.color));
    }
    if (changedProperties.has("thickness")) {
      this.style.setProperty("--divider-thickness", this.thickness);
    }
    if (changedProperties.has("margin")) {
      this.style.setProperty("--divider-margin", this.margin);
    }
    if (changedProperties.has("height")) {
      this.style.setProperty("--divider-height", this.height);
    }
  }

  /**
   * Gets the current computed color of the divider.
   * @returns Resolved color value
   * @public
   */
  public getComputedColor(): string {
    return (
      getComputedStyle(this).getPropertyValue("--divider-color").trim() ||
      this.color
    );
  }

  /**
   * Gets the current computed thickness of the divider.
   * @returns Resolved thickness value
   * @public
   */
  public getComputedThickness(): string {
    return (
      getComputedStyle(this).getPropertyValue("--divider-thickness").trim() ||
      this.thickness
    );
  }

  /** @protected */
  override render() {
    const isVertical = this.direction === "vertical";
    const verticalClasses = classMap({
      vertical: true,
      inset: this.inset,
    });
    const horizontalClasses = classMap({
      horizontal: true,
      inset: this.inset,
    });

    return when(
      isVertical,
      () => html`
        <div
          class=${verticalClasses}
          part="divider"
          role="separator"
          aria-orientation="vertical"
        ></div>
      `,
      () => html`
        <hr
          class=${horizontalClasses}
          part="divider"
          role="separator"
          aria-orientation="horizontal"
        />
      `,
    );
  }
}
